/**
 * Face ID sign-in for the native builds (Face ID / Touch ID on iOS,
 * fingerprint or face on Android).
 *
 * This is NOT the same feature as the passkey sign-in in pwa/BiometricToggle —
 * that one proves WHO you are to the server with a WebAuthn credential. This
 * one proves the phone is still in its owner's hands before revealing a CRM
 * that is already signed in. The passkey flow cannot work inside the native
 * shell at all: the WebAuthn RP-ID is phiegcrm.com and the app's origin is
 * https://localhost, so the browser refuses the credential.
 *
 * Everything here is a no-op on the web, so callers need no platform guard.
 */
import { Capacitor, registerPlugin } from "@capacitor/core";
import jwt_decode from "jwt-decode";
import { createLogger, summarizeError } from "../utils/logger";

const log = createLogger("native.biometric");

// The native code comes from @aparajita/capacitor-biometric-auth, which is
// installed in the SHELL project (where `cap sync` picks up its Swift/Kotlin).
// We deliberately do NOT import its JavaScript here: that package is strict
// ESM whose own build emits extensionless relative imports, which webpack 5
// refuses to resolve, and it broke the CRM build outright. Capacitor's bridge
// gives us the same native methods without the package's JS layer.
//
// `authenticate()` in that package is only a thin JS wrapper that calls the
// native `internalAuthenticate` and re-labels the error, so calling the native
// method directly loses nothing we use.
const BiometricAuthNative = registerPlugin("BiometricAuthNative");

// Mirrors the package's BiometryType enum; these values are part of the
// native contract, not of its JS layer.
const BIOMETRY_LABELS = {
  1: "Touch ID",
  2: "Face ID",
  3: "fingerprint",
  4: "face unlock",
  5: "iris unlock",
};

// One key per user id: two people sharing a handset must not inherit each
// other's setting, and signing out of one account leaves the other's intact.
const prefKey = (userId) => `appLock.enabled.${userId || "unknown"}`;

export const isNativeApp = () => Capacitor.isNativePlatform();
export const nativePlatform = () => Capacitor.getPlatform();

/**
 * What the device can actually do, so the settings panel can name the right
 * thing ("Face ID" vs "fingerprint") instead of guessing.
 * @returns {Promise<{available: boolean, label: string}>}
 */
export async function checkDeviceBiometry() {
  if (!isNativeApp()) return { available: false, deviceIsSecure: false, label: "" };

  try {
    const info = await BiometricAuthNative.checkBiometry();

    return {
      available: Boolean(info?.isAvailable),
      // True when the phone has ANY secure lock — biometrics or just a PIN /
      // pattern. authenticate() passes allowDeviceCredential, so the app lock
      // is perfectly usable on a phone whose owner never enrolled a
      // fingerprint; gating on biometrics alone shut those users out.
      deviceIsSecure: Boolean(info?.deviceIsSecure),
      label: BIOMETRY_LABELS[info?.biometryType] || "biometrics",
      // Present when the device supports biometry but it is unusable right
      // now (nothing enrolled, locked out after failed attempts).
      reason: info?.reason || "",
    };
  } catch (error) {
    log.error("biometry.check_failed", { ...summarizeError(error) });
    return { available: false, deviceIsSecure: false, label: "" };
  }
}

// A successful verification stands for a few seconds. Two features can ask
// back-to-back — Face ID sign-in restores the session and the lock then
// evaluates it; enabling the lock confirms identity and the gate then locks —
// and the person should be asked once, not twice. Ten seconds is far too
// short for anyone else to have picked the phone up in between.
const VERIFY_GRACE_MS = 10_000;
let lastVerifiedAt = 0;

/**
 * Prompt for the device biometric.
 * @returns {Promise<boolean>} true only on a successful verification.
 */
export async function verifyIdentity(reason = "Unlock Phieg") {
  if (!isNativeApp()) return true;
  if (Date.now() - lastVerifiedAt < VERIFY_GRACE_MS) return true;

  try {
    await BiometricAuthNative.internalAuthenticate({
      reason,
      cancelTitle: "Cancel",
      // The device passcode is an accepted fallback: a user whose face is not
      // recognised (mask, dark room, wet finger) must still be able to get in
      // without being forced to sign out and retype a password.
      allowDeviceCredential: true,
      iosFallbackTitle: "Use passcode",
      androidTitle: "Unlock Phieg",
      androidSubtitle: reason,
      androidConfirmationRequired: false,
    });
    lastVerifiedAt = Date.now();
    return true;
  } catch (error) {
    // A cancel is an ordinary outcome, not a fault — log at debug volume only.
    log.info("biometry.verify_rejected", { code: error?.code });
    return false;
  }
}

/** Is Face ID sign-in switched on for this user on this device? */
export async function isBiometricSignInEnabled(userId) {
  if (!isNativeApp()) return false;
  try {
    const { Preferences } = await import("@capacitor/preferences");
    const { value } = await Preferences.get({ key: prefKey(userId) });
    return value === "true";
  } catch (error) {
    log.error("biometry.pref_read_failed", { ...summarizeError(error) });
    return false;
  }
}

/**
 * Turn Face ID sign-in on or off. Enabling demands a successful verification
 * first, so nobody can switch on a sign-in they cannot themselves pass.
 *
 * Until 2026-10-23 this same switch also locked the app behind Face ID on
 * every return from the background (AppLockGate). Mostafa dropped the lock:
 * Face ID is a way to sign in on the login screen, nothing more.
 * @returns {Promise<boolean>} the value actually stored.
 */
export async function setBiometricSignInEnabled(userId, enabled) {
  if (!isNativeApp()) return false;

  if (enabled) {
    const ok = await verifyIdentity("Confirm it's you to turn on Face ID sign-in");
    if (!ok) return await isBiometricSignInEnabled(userId);
  }

  try {
    const { Preferences } = await import("@capacitor/preferences");
    await Preferences.set({ key: prefKey(userId), value: String(enabled) });
    // Switching it off is also "forget this device": the quick sign-in copy
    // of the session must not outlive the setting that protects it.
    if (!enabled) await clearBiometricSession();
    return enabled;
  } catch (error) {
    log.error("biometry.pref_write_failed", { ...summarizeError(error) });
    return await isBiometricSignInEnabled(userId);
  }
}

// ---------------------------------------------------------------------------
// Quick sign-in vault.
//
// Logging out clears localStorage, which is the only place the web app keeps
// the session — so after a logout there is nothing for Face ID to unlock and
// the user is back to a password. Banking apps solve this by keeping a
// biometric-protected copy of the session on the device; this is that.
//
// From native 1.1.0 the copy lives in the iOS Keychain (Android: the
// Keystore-backed store) through the SecureStorage plugin
// (@aparajita/capacitor-secure-storage, installed in the shell and reached
// through the bridge like BiometricAuthNative — no JS dependency here).
// Readable only while the phone is unlocked, never synced to iCloud, never
// restored onto another device. A store build older than 1.1.0 has no such
// plugin; there the vault is Capacitor Preferences (UserDefaults), which is
// what it always was. A copy left in Preferences by an older build is moved
// into the keychain the first time the keychain answers.
//
// It is only ever written while Face ID sign-in is ON for that user, and it
// is wiped when that is turned off, when the token expires, or when reading
// it fails for any reason.
// ---------------------------------------------------------------------------
const SESSION_KEY = "appLock.session";
// The plugin stores every item under a prefix; this is the full keychain key.
const KEYCHAIN_SESSION_KEY = "phieg_appLock.session";
// KeychainAccess.whenUnlockedThisDeviceOnly in the plugin's enum.
const KEYCHAIN_ACCESS_THIS_DEVICE_ONLY = 1;

const SecureStorageNative = registerPlugin("SecureStorage");

// Whether this store build has the keychain plugin: unknown until the first
// call, then remembered. Only "the plugin does not exist" flips it to false;
// a keychain error on a build that has it is an ordinary failure.
let keychainAvailable = null;

const isUnimplemented = (error) =>
  error?.code === "UNIMPLEMENTED" ||
  /not implemented|not available|no implementation/i.test(String(error?.message || ""));

const keychainCall = async (fn) => {
  if (keychainAvailable === false) return { available: false };
  try {
    const result = await fn();
    keychainAvailable = true;
    return { available: true, result };
  } catch (error) {
    if (isUnimplemented(error)) {
      keychainAvailable = false;
      return { available: false };
    }
    throw error;
  }
};

const keychainGet = () =>
  keychainCall(async () => {
    const { data } = await SecureStorageNative.internalGetItem({
      prefixedKey: KEYCHAIN_SESSION_KEY,
      sync: false,
    });
    return typeof data === "string" && data ? data : null;
  });

const keychainSet = (value) =>
  keychainCall(() =>
    SecureStorageNative.internalSetItem({
      prefixedKey: KEYCHAIN_SESSION_KEY,
      data: value,
      sync: false,
      access: KEYCHAIN_ACCESS_THIS_DEVICE_ONLY,
    })
  );

const keychainRemove = () =>
  keychainCall(() =>
    SecureStorageNative.internalRemoveItem({
      prefixedKey: KEYCHAIN_SESSION_KEY,
      sync: false,
    })
  );

async function preferencesGet() {
  const { Preferences } = await import("@capacitor/preferences");
  const { value } = await Preferences.get({ key: SESSION_KEY });
  return value || null;
}

async function preferencesSet(value) {
  const { Preferences } = await import("@capacitor/preferences");
  await Preferences.set({ key: SESSION_KEY, value });
}

async function preferencesRemove() {
  const { Preferences } = await import("@capacitor/preferences");
  await Preferences.remove({ key: SESSION_KEY });
}

/** The vault's copy of the session, as the JSON string it was stored as. */
async function vaultRead() {
  const keychain = await keychainGet();
  if (!keychain.available) return preferencesGet();
  if (keychain.result) return keychain.result;
  // Nothing in the keychain: a copy left in Preferences by an older build
  // moves in now, so the upgrade never asks for a password it did not need.
  const legacy = await preferencesGet();
  if (legacy) {
    const moved = await keychainSet(legacy);
    if (moved.available) await preferencesRemove();
  }
  return legacy;
}

async function vaultWrite(value) {
  const keychain = await keychainSet(value);
  if (keychain.available) return;
  await preferencesSet(value);
}

async function vaultRemove() {
  // Both stores, so no copy survives in the one an older build used.
  await keychainRemove();
  await preferencesRemove();
}

const tokenIsLive = (token) => {
  try {
    const { exp } = jwt_decode(token);
    return typeof exp === "number" && exp * 1000 > Date.now();
  } catch {
    return false;
  }
};

/** Forget the quick sign-in copy. Safe to call when none exists. */
export async function clearBiometricSession() {
  if (!isNativeApp()) return;
  try {
    await vaultRemove();
  } catch (error) {
    log.error("biometry.session_clear_failed", { ...summarizeError(error) });
  }
}

/**
 * Keep a copy of the signed-in session for Face ID sign-in. No-op unless the
 * Face ID sign-in is on for this user — that switch is the user's consent.
 * @param {{user: object, token: string}} session
 */
export async function saveBiometricSession(session) {
  if (!isNativeApp()) return;
  const userId = session?.user?._id;
  if (!userId || !session?.token || !tokenIsLive(session.token)) return;
  if (!(await isBiometricSignInEnabled(userId))) return;
  try {
    await vaultWrite(JSON.stringify({ user: session.user, token: session.token }));
  } catch (error) {
    log.error("biometry.session_save_failed", { ...summarizeError(error) });
  }
}

/**
 * The saved session, if there is one worth offering: token still valid and
 * Face ID sign-in still on for that user. Anything else is wiped and null returned,
 * so the login screen never shows a Face ID button that cannot work.
 * @returns {Promise<{user: object, token: string} | null>}
 */
export async function getBiometricSession() {
  if (!isNativeApp()) return null;
  try {
    const value = await vaultRead();
    if (!value) return null;
    const session = JSON.parse(value);
    const ok =
      session?.user?._id &&
      session?.token &&
      tokenIsLive(session.token) &&
      (await isBiometricSignInEnabled(session.user._id));
    if (!ok) {
      await clearBiometricSession();
      return null;
    }
    return session;
  } catch (error) {
    log.error("biometry.session_read_failed", { ...summarizeError(error) });
    await clearBiometricSession();
    return null;
  }
}
